import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Building, RetrofitPotential } from '../types/building';
import { BuildingDataService } from '../services/buildingDataService';
import { LL97Service } from '../services/ll97Service';
import { RetrofitService } from '../services/retrofitService';
import { EconomicOptimizerService, EconomicChoice } from '../services/economicOptimizerService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface BuildingDetailPageProps {
    buildings: Building[];
    onBack: () => void;
}

interface RetrofitOption {
    id: string;
    name: string;
    description: string;
    cost: number;
    savingsKwh: number;
    savingsTherm: number;
    emissionReduction: number;
    paybackYears: number;
    roi: number;
}

interface CPACELender {
    name: string;
    website: string;
    specialties: string[];
    minLoanAmount: number;
    maxLoanAmount: number;
    partnershipContact?: {
        name: string;
        title: string;
        email: string;
        phone: string;
        directLine?: string;
    };
    portfolioMinimum?: number;
    partnershipBenefits?: string[];
}

interface Contractor {
    name: string;
    specialty: string;
    location: string;
    rating: number;
    website?: string;
    partnershipContact?: {
        name: string;
        title: string;
        email: string;
        phone: string;
    };
    certifications?: string[];
    partnershipProgram?: {
        referralCommission?: string;
        volumeDiscounts?: string;
        exclusiveTerritories?: boolean;
    };
}

interface GrantIncentive {
    name: string;
    type: 'Federal' | 'State' | 'City' | 'Utility';
    amount: string;
    description: string;
    eligibility: string;
    deadline?: string;
    link?: string;
}

const getBuildingTypeDescription = (buildingType: string): string => {
    const descriptions: { [key: string]: string } = {
        'Office': 'Commercial buildings primarily used for business operations, typically with high lighting and HVAC demands',
        'Multifamily Housing': 'Residential buildings with multiple separate housing units, often apartments or condominiums',
        'Mixed Use': 'Buildings combining residential, commercial, and/or retail spaces within the same structure',
        'Retail': 'Commercial buildings primarily used for selling goods and services to consumers',
        'Hotel': 'Hospitality buildings providing temporary accommodation with 24/7 energy demands',
        'Warehouse and Storage': 'Industrial buildings primarily used for storage with typically lower energy intensity',
        'Other': 'Specialized building types including institutional, educational, or unique-purpose facilities'
    };
    return descriptions[buildingType] || 'Building type with specific energy consumption patterns';
};

const BuildingDetailPage: React.FC<BuildingDetailPageProps> = ({ buildings, onBack }) => {
    const { buildingId } = useParams<{ buildingId: string }>();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [emissionsGap, setEmissionsGap] = useState<any>(null);
    const [retrofitOptions, setRetrofitOptions] = useState<RetrofitOption[]>([]);
    const [selectedRetrofits, setSelectedRetrofits] = useState<string[]>([]);
    const [selectedIncentives, setSelectedIncentives] = useState<{ [retrofitId: string]: string[] }>({});
    const [showCalc, setShowCalc] = useState<{ type: string; id: string } | null>(null);
    const [selectedAnalysisType, setSelectedAnalysisType] = useState<string>('');
    const [economicChoices, setEconomicChoices] = useState<EconomicChoice[]>([]);

    // Enhanced partnership data with contact information
    const cpaceLenders: CPACELender[] = [
        {
            name: "Renovate America",
            website: "https://renovateamerica.com",
            specialties: ["Solar", "HVAC", "Windows", "Insulation"],
            minLoanAmount: 5000,
            maxLoanAmount: 500000,
            portfolioMinimum: 100000,
            partnershipContact: {
                name: "Sarah Martinez",
                title: "Partnership Development Director",
                email: "partnerships@renovateamerica.com",
                phone: "(855) 736-6828",
                directLine: "(855) 736-6828 ext. 2401"
            },
            partnershipBenefits: [
                "3-5% referral commission on funded projects",
                "Co-marketing opportunities",
                "Priority processing for partner referrals",
                "Dedicated account management"
            ]
        },
        {
            name: "Ygrene Energy Fund",
            website: "https://ygrene.com",
            specialties: ["Energy Efficiency", "Solar", "Water Conservation"],
            minLoanAmount: 10000,
            maxLoanAmount: 1000000,
            portfolioMinimum: 250000,
            partnershipContact: {
                name: "Michael Chen",
                title: "Business Development Manager",
                email: "partnerships@ygrene.com",
                phone: "(855) 937-3631",
                directLine: "(855) 937-3631 ext. 1205"
            },
            partnershipBenefits: [
                "Up to 5% commission on funded volume",
                "Marketing development funds available",
                "Joint sales training programs",
                "Exclusive territory opportunities"
            ]
        },
        {
            name: "Petros PACE Finance",
            website: "https://petrospace.com",
            specialties: ["Commercial Buildings", "Multifamily"],
            minLoanAmount: 25000,
            maxLoanAmount: 5000000,
            portfolioMinimum: 500000,
            partnershipContact: {
                name: "David Rodriguez",
                title: "Strategic Partnerships VP",
                email: "partnerships@petrospace.com",
                phone: "(877) 737-8767",
                directLine: "(877) 737-8767 ext. 3401"
            },
            partnershipBenefits: [
                "4-6% commission on commercial projects",
                "White-label financing solutions",
                "Dedicated underwriting team",
                "Annual volume bonuses available"
            ]
        },
        {
            name: "Counterpointe Energy Solutions",
            website: "https://counterpointees.com", 
            specialties: ["Large Commercial", "Energy Storage", "Solar"],
            minLoanAmount: 50000,
            maxLoanAmount: 10000000,
            portfolioMinimum: 1000000,
            partnershipContact: {
                name: "Jennifer Liu",
                title: "Channel Partner Manager",
                email: "channels@counterpointees.com",
                phone: "(800) 580-3228",
                directLine: "(800) 580-3228 ext. 4501"
            },
            partnershipBenefits: [
                "5-7% commission on large projects ($1M+)",
                "Joint proposal development support",
                "Technical advisory services",
                "Performance-based bonus structure"
            ]
        }
    ];

    const contractors: Contractor[] = [
        {
            name: "Green Energy Solutions NYC",
            specialty: "Solar & Energy Efficiency",
            location: "Manhattan",
            rating: 4.8,
            website: "https://greenenergy-nyc.com",
            certifications: ["NABCEP Certified", "BPI Certified", "NYSERDA Approved"],
            partnershipContact: {
                name: "Robert Thompson",
                title: "Business Development Director",
                email: "partnerships@greenenergy-nyc.com",
                phone: "(212) 555-0142"
            },
            partnershipProgram: {
                referralCommission: "2-4% of project value",
                volumeDiscounts: "Volume discounts available for 10+ referrals annually",
                exclusiveTerritories: true
            }
        },
        {
            name: "NYC HVAC Specialists",
            specialty: "HVAC Systems",
            location: "Brooklyn", 
            rating: 4.6,
            website: "https://nychvacpro.com",
            certifications: ["EPA Certified", "NATE Certified", "Local Law 87 Certified"],
            partnershipContact: {
                name: "Maria Gonzalez",
                title: "Partner Relations Manager",
                email: "partners@nychvacpro.com",
                phone: "(718) 555-0198"
            },
            partnershipProgram: {
                referralCommission: "3-5% of project value",
                volumeDiscounts: "Tiered pricing for partner projects",
                exclusiveTerritories: false
            }
        },
        {
            name: "Empire Insulation Co.",
            specialty: "Building Envelope",
            location: "Queens",
            rating: 4.7,
            website: "https://empireinsulation.nyc",
            certifications: ["BPI Certified", "RESNET Certified", "Energy Star Partner"],
            partnershipContact: {
                name: "James Wilson",
                title: "Strategic Partnerships Lead",
                email: "partnerships@empireinsulation.nyc", 
                phone: "(347) 555-0176"
            },
            partnershipProgram: {
                referralCommission: "3% of project value",
                volumeDiscounts: "5-15% volume discounts based on annual referrals",
                exclusiveTerritories: true
            }
        },
        {
            name: "Metro Window & Solar",
            specialty: "Windows & Solar Installation",
            location: "Bronx",
            rating: 4.5,
            website: "https://metrowindowsolar.com",
            certifications: ["AAMA Certified", "NABCEP Solar Certified", "OSHA Certified"],
            partnershipContact: {
                name: "Lisa Patel",
                title: "Channel Development Manager",
                email: "channels@metrowindowsolar.com",
                phone: "(718) 555-0123"
            },
            partnershipProgram: {
                referralCommission: "2-3% of project value",
                volumeDiscounts: "Custom pricing for high-volume partners",
                exclusiveTerritories: false
            }
        },
        {
            name: "Urban Energy Solutions",
            specialty: "Commercial Energy Management",
            location: "Manhattan",
            rating: 4.9,
            website: "https://urbanenergysolutions.com",
            certifications: ["LEED AP", "CEM Certified", "ASHRAE Member"],
            partnershipContact: {
                name: "Ahmed Hassan",
                title: "Business Development VP",
                email: "bd@urbanenergysolutions.com",
                phone: "(212) 555-0234"
            },
            partnershipProgram: {
                referralCommission: "4-6% of project value",
                volumeDiscounts: "Preferred partner pricing tiers",
                exclusiveTerritories: true
            }
        }
    ];

    const grantsIncentives: GrantIncentive[] = [
        {
            name: "NYSERDA Commercial Incentive",
            type: "State",
            amount: "$2/sq ft (up to $200,000)",
            description: "For energy efficiency retrofits in commercial buildings",
            eligibility: "Commercial buildings over 25,000 sq ft",
            deadline: "12/31/2025",
            link: "https://www.nyserda.ny.gov/All-Programs/Programs/Commercial-Programs"
        },
        {
            name: "Con Edison Energy Efficiency Rebate",
            type: "Utility",
            amount: "Up to $50,000",
            description: "For lighting, HVAC, and envelope upgrades",
            eligibility: "NYC buildings served by Con Edison",
            deadline: "Rolling basis",
            link: "https://www.coned.com/en/save-money/rebates-incentives-tax-credits/rebates-incentives-tax-credits-for-commercial-industrial-buildings"
        },
        {
            name: "Federal Investment Tax Credit",
            type: "Federal",
            amount: "30% of project cost",
            description: "For solar and renewable energy systems",
            eligibility: "All commercial properties",
            deadline: "12/31/2032",
            link: "https://www.energy.gov/eere/solar/homeowners-guide-federal-tax-credit-solar-photovoltaics"
        }
    ];

    // Filter buildings based on search
    const filteredBuildings = useMemo(() => {
        if (!searchQuery.trim()) return [];
        return buildings.filter(building =>
            building.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
            building.propertyName.toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 10); // Limit suggestions
    }, [searchQuery, buildings]);

    // Calculate emissions gap when building is selected
    useEffect(() => {
        if (selectedBuilding) {
            calculateEmissionsGap(selectedBuilding);
            generateRetrofitOptions(selectedBuilding);
        }
    }, [selectedBuilding]);

    // Auto-select building from URL parameter
    useEffect(() => {
        if (buildingId && buildings.length > 0) {
            const building = buildings.find(b => b.id === buildingId);
            if (building) {
                setSelectedBuilding(building);
                setSearchQuery(building.address || building.propertyName);
                setShowSuggestions(false);
            }
        }
    }, [buildingId, buildings]);

    // Load economic choices when building is selected
    useEffect(() => {
        if (selectedBuilding) {
            const choices = EconomicOptimizerService.getAllAnalysisChoices(selectedBuilding);
            setEconomicChoices(choices);
        }
    }, [selectedBuilding]);

    const calculateEmissionsGap = (building: Building) => {
        const ll97LimitPerSqFt = LL97Service.getEmissionLimit(building.buildingType, 2025);
        const currentEmissions = building.totalGHGEmissions;
        const allowedEmissions = ll97LimitPerSqFt * building.grossFloorArea;
        
        // Gap = Current Emissions - Allowed Emissions (only if current exceeds allowed)
        const gapAmount = Math.max(0, currentEmissions - allowedEmissions);
        const gapPerSqFt = gapAmount / building.grossFloorArea;
        
        const finePerTon = 268; // LL97 fine rate ($268 per metric ton CO2e)
        // Fix: Only calculate fine if there is a gap
        const annualFine = gapAmount > 0 ? gapAmount * finePerTon : 0;
        
        setEmissionsGap({
            currentEmissions,
            allowedEmissions,
            gapAmount,
            gapPerSqFt,
            annualFine,
            complianceStatus: gapAmount > 0 ? 'Non-Compliant' : 'Compliant'
        });
    };

    const generateRetrofitOptions = (building: Building) => {
        const baselineCost = building.grossFloorArea * 15; // $15/sq ft baseline
        
        const options: RetrofitOption[] = [
            {
                id: 'hvac-upgrade',
                name: 'HVAC System Upgrade',
                description: 'High-efficiency heat pumps and smart controls',
                cost: baselineCost * 1.2,
                savingsKwh: building.electricityUse * 0.25,
                savingsTherm: building.naturalGasUse * 0.30,
                emissionReduction: building.totalGHGEmissions * 0.35,
                paybackYears: 8.5,
                roi: 12.8
            },
            {
                id: 'building-envelope',
                name: 'Building Envelope Improvements',
                description: 'Insulation, air sealing, and window upgrades',
                cost: baselineCost * 0.8,
                savingsKwh: building.electricityUse * 0.15,
                savingsTherm: building.naturalGasUse * 0.25,
                emissionReduction: building.totalGHGEmissions * 0.20,
                paybackYears: 12.2,
                roi: 8.9
            },
            {
                id: 'lighting-upgrade',
                name: 'LED Lighting & Controls',
                description: 'LED fixtures with occupancy and daylight sensors',
                cost: baselineCost * 0.3,
                savingsKwh: building.electricityUse * 0.20,
                savingsTherm: 0,
                emissionReduction: building.totalGHGEmissions * 0.12,
                paybackYears: 4.8,
                roi: 22.1
            },
            {
                id: 'solar-installation',
                name: 'Rooftop Solar System',
                description: 'Photovoltaic system with battery storage',
                cost: baselineCost * 2.5,
                savingsKwh: building.electricityUse * 0.40,
                savingsTherm: 0,
                emissionReduction: building.totalGHGEmissions * 0.30,
                paybackYears: 11.5,
                roi: 9.6
            },
            {
                id: 'energy-management',
                name: 'Energy Management System',
                description: 'Smart building automation and monitoring',
                cost: baselineCost * 0.4,
                savingsKwh: building.electricityUse * 0.10,
                savingsTherm: building.naturalGasUse * 0.15,
                emissionReduction: building.totalGHGEmissions * 0.15,
                paybackYears: 6.2,
                roi: 17.3
            }
        ];
        
        setRetrofitOptions(options);
    };

    const handleBuildingSelect = (building: Building) => {
        setSelectedBuilding(building);
        setSearchQuery(building.address);
        setShowSuggestions(false);
    };

    const handleAnalysisTypeSelect = (analysisType: string) => {
        if (selectedBuilding) {
            setSelectedAnalysisType(analysisType);
            // Navigate to Economic Optimizer with the selected building and analysis type
            navigate(`/economic-optimizer/${selectedBuilding.id}?analysisType=${analysisType}`);
        }
    };

    const toggleRetrofitSelection = (retrofitId: string) => {
        setSelectedRetrofits(prev => 
            prev.includes(retrofitId) 
                ? prev.filter(id => id !== retrofitId)
                : [...prev, retrofitId]
        );
    };

    const handleIncentiveToggle = (retrofitId: string, incentiveName: string) => {
        setSelectedIncentives(prev => {
            const current = prev[retrofitId] || [];
            if (current.includes(incentiveName)) {
                return { ...prev, [retrofitId]: current.filter(i => i !== incentiveName) };
            } else {
                return { ...prev, [retrofitId]: [...current, incentiveName] };
            }
        });
    };

    const getApplicableIncentives = (retrofit: RetrofitOption) => {
        if (retrofit.name.toLowerCase().includes('solar')) {
            return grantsIncentives.filter(g => g.name.includes('Federal Investment Tax Credit'));
        }
        if (retrofit.name.toLowerCase().includes('lighting')) {
            return grantsIncentives.filter(g => g.name.includes('Con Edison'));
        }
        if (retrofit.name.toLowerCase().includes('hvac') || retrofit.name.toLowerCase().includes('envelope')) {
            return grantsIncentives.filter(g => g.name.includes('NYSERDA') || g.name.includes('Con Edison'));
        }
        // Default: all state/utility incentives
        return grantsIncentives.filter(g => g.type !== 'Federal');
    };

    // Helper to calculate the actual rebate amount for an incentive
    const getActualRebateAmount = (incentive: GrantIncentive, retrofit: RetrofitOption) => {
        if (!selectedBuilding) return null;
        // NYSERDA: $2/sq ft, max $200,000
        if (incentive.name === 'NYSERDA Commercial Incentive') {
            const amt = Math.min(200000, selectedBuilding.grossFloorArea * 2);
            return amt > 0 ? `$${amt.toLocaleString()}` : null;
        }
        // Con Edison: Up to $50,000 (show max)
        if (incentive.name === 'Con Edison Energy Efficiency Rebate') {
            return '$50,000';
        }
        // Federal ITC: 30% of project cost
        if (incentive.name === 'Federal Investment Tax Credit') {
            const amt = Math.round(retrofit.cost * 0.3);
            return amt > 0 ? `$${amt.toLocaleString()}` : null;
        }
        return null;
    };

    // Adjust cost for selected incentives (simple demo logic)
    const getRetrofitCostAfterIncentives = (retrofit: RetrofitOption) => {
        let cost = retrofit.cost;
        const selected = selectedIncentives[retrofit.id] || [];
        selected.forEach(incentiveName => {
            if (incentiveName === 'Federal Investment Tax Credit') cost *= 0.7; // 30% off
            if (incentiveName === 'NYSERDA Commercial Incentive') cost -= Math.min(200000, (selectedBuilding?.grossFloorArea || 0) * 2);
            if (incentiveName === 'Con Edison Energy Efficiency Rebate') cost -= 50000;
        });
        return Math.max(cost, 0);
    };

    const calculateCombinedBenefits = () => {
        const selected = retrofitOptions.filter(option => selectedRetrofits.includes(option.id));
        
        // Use compound reduction for emissions (diminishing returns)
        const emissionReductions = selected.map(option => option.emissionReduction);
        const combinedEmissionReduction = emissionReductions.reduce((combined, reduction) => {
            return combined + reduction * (1 - combined / 100);
        }, 0);
        
        // For energy savings, calculate them proportionally to emission reductions
        // This reflects that energy savings often correlate with emission reductions
        const totalEmissions = emissionReductions.reduce((sum, reduction) => sum + reduction, 0);
        const emissionWeight = totalEmissions > 0 ? combinedEmissionReduction / totalEmissions : 1;
        
        const totalKwhSavings = selected.reduce((sum, option) => sum + option.savingsKwh, 0);
        const totalThermSavings = selected.reduce((sum, option) => sum + option.savingsTherm, 0);
        
        return {
            totalCost: selected.reduce((sum, option) => sum + getRetrofitCostAfterIncentives(option), 0),
            totalSavingsKwh: Math.round(totalKwhSavings * emissionWeight),
            totalSavingsTherm: Math.round(totalThermSavings * emissionWeight),
            totalEmissionReduction: Math.round(combinedEmissionReduction * 100) / 100, // Round to 2 decimal places
            averagePayback: selected.length > 0 
                ? selected.reduce((sum, option) => sum + option.paybackYears, 0) / selected.length 
                : 0
        };
    };

    const combinedBenefits = calculateCombinedBenefits();

    // Helper to get calculation explanation for cost
    const getCostExplanation = (retrofit: RetrofitOption) => {
        return `Cost = Gross Floor Area × $/sq ft\nFor this building: ${selectedBuilding?.grossFloorArea.toLocaleString()} sq ft × $15/sq ft (baseline) × factor\nFactor depends on retrofit type (e.g., 1.2 for HVAC, 0.8 for Envelope, etc.)`;
    };
    // Helper to get calculation explanation for emission reduction
    const getEmissionReductionExplanation = (retrofit: RetrofitOption) => {
        return `Emission Reduction = Total GHG Emissions × % Reduction\nFor this building: ${selectedBuilding?.totalGHGEmissions.toLocaleString()} tons × % (varies by retrofit)\nExample: 35% for HVAC, 20% for Envelope, etc.`;
    };

    // Export functionality for building analysis summary
    const generatePrintableSummary = () => {
        if (!selectedBuilding) return;

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const selected = retrofitOptions.filter(option => selectedRetrofits.includes(option.id));
        const combinedBenefits = calculateCombinedBenefits();

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Building Energy Retrofit Analysis - ${selectedBuilding.propertyName || selectedBuilding.address}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
                    .header { text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
                    .logo { font-size: 24px; font-weight: bold; color: #2563eb; }
                    .building-info { background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
                    .section { margin: 30px 0; page-break-inside: avoid; }
                    .section-title { color: #2563eb; font-size: 18px; font-weight: bold; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 15px; }
                    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
                    .card { border: 1px solid #e5e7eb; padding: 15px; border-radius: 6px; }
                    .metric { display: flex; justify-content: space-between; margin: 8px 0; }
                    .metric-label { font-weight: 500; }
                    .metric-value { color: #059669; font-weight: bold; }
                    .contact-info { background: #fef3c7; padding: 15px; border-radius: 6px; margin: 10px 0; }
                    .partnership-benefits { font-size: 14px; color: #6b7280; margin-top: 8px; }
                    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; }
                    @media print { body { margin: 20px; } .no-print { display: none; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="logo">NYC Energy Retrofit Analyzer</div>
                    <h1>Building Energy Analysis Report</h1>
                    <p>Generated on ${new Date().toLocaleDateString()}</p>
                </div>

                <div class="building-info">
                    <h2>Building Information</h2>
                    <div class="grid">
                        <div>
                            <div class="metric">
                                <span class="metric-label">Property Name:</span>
                                <span class="metric-value">${selectedBuilding.propertyName || 'N/A'}</span>
                            </div>
                            <div class="metric">
                                <span class="metric-label">Address:</span>
                                <span class="metric-value">${selectedBuilding.address}</span>
                            </div>
                            <div class="metric">
                                <span class="metric-label">Building Type:</span>
                                <span class="metric-value">${selectedBuilding.buildingType}</span>
                            </div>
                            <div class="metric">
                                <span class="metric-label">Building Type:</span>
                                <span class="metric-value">${selectedBuilding.buildingType}</span>
                            </div>
                        </div>
                        <div>
                            <div class="metric">
                                <span class="metric-label">Gross Floor Area:</span>
                                <span class="metric-value">${selectedBuilding.grossFloorArea.toLocaleString()} sq ft</span>
                            </div>
                            <div class="metric">
                                <span class="metric-label">Current GHG Emissions:</span>
                                <span class="metric-value">${selectedBuilding.totalGHGEmissions.toFixed(1)} tons CO2e</span>
                            </div>
                            <div class="metric">
                                <span class="metric-label">Energy Use Intensity:</span>
                                <span class="metric-value">${selectedBuilding.siteEUI.toFixed(1)} kBtu/sq ft</span>
                            </div>
                            <div class="metric">
                                <span class="metric-label">ENERGY STAR Score:</span>
                                <span class="metric-value">${selectedBuilding.energyStarScore || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                ${emissionsGap ? `
                <div class="section">
                    <h2 class="section-title">Local Law 97 Compliance Analysis</h2>
                    <div class="card">
                        <div class="metric">
                            <span class="metric-label">2025 Emission Limit:</span>
                            <span class="metric-value">${emissionsGap.limitPerSqFt.toFixed(3)} tons CO2e/sq ft</span>
                        </div>
                        <div class="metric">
                            <span class="metric-label">Current Emissions:</span>
                            <span class="metric-value">${emissionsGap.currentEmissionsPerSqFt.toFixed(3)} tons CO2e/sq ft</span>
                        </div>
                        <div class="metric">
                            <span class="metric-label">Gap to Close:</span>
                            <span class="metric-value">${emissionsGap.gapPerSqFt.toFixed(3)} tons CO2e/sq ft</span>
                        </div>
                        <div class="metric">
                            <span class="metric-label">Total Gap:</span>
                            <span class="metric-value">${emissionsGap.totalGap.toFixed(1)} tons CO2e</span>
                        </div>
                        <div class="metric">
                            <span class="metric-label">Potential Annual Fine:</span>
                            <span class="metric-value">$${emissionsGap.annualFine.toLocaleString()}</span>
                        </div>
                    </div>
                </div>
                ` : ''}

                ${selected.length > 0 ? `
                <div class="section">
                    <h2 class="section-title">Selected Retrofit Measures</h2>
                    <div class="grid">
                        ${selected.map(retrofit => `
                            <div class="card">
                                <h3>${retrofit.name}</h3>
                                <p>${retrofit.description}</p>
                                <div class="metric">
                                    <span class="metric-label">Cost (after incentives):</span>
                                    <span class="metric-value">$${getRetrofitCostAfterIncentives(retrofit).toLocaleString()}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Annual kWh Savings:</span>
                                    <span class="metric-value">${retrofit.savingsKwh.toLocaleString()} kWh</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Annual Therm Savings:</span>
                                    <span class="metric-value">${retrofit.savingsTherm.toLocaleString()} therms</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Emission Reduction:</span>
                                    <span class="metric-value">${retrofit.emissionReduction.toFixed(1)} tons CO2e</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Payback Period:</span>
                                    <span class="metric-value">${retrofit.paybackYears.toFixed(1)} years</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">ROI:</span>
                                    <span class="metric-value">${retrofit.roi.toFixed(1)}%</span>
                                </div>
                            </div>
                        `).join('')}
                    </div>

                    <div class="card" style="margin-top: 20px; background: #f0f9ff;">
                        <h3>Combined Project Benefits</h3>
                        <div class="grid">
                            <div>
                                <div class="metric">
                                    <span class="metric-label">Total Investment:</span>
                                    <span class="metric-value">$${combinedBenefits.totalCost.toLocaleString()}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Total kWh Savings:</span>
                                    <span class="metric-value">${combinedBenefits.totalSavingsKwh.toLocaleString()} kWh/year</span>
                                </div>
                            </div>
                            <div>
                                <div class="metric">
                                    <span class="metric-label">Total Therm Savings:</span>
                                    <span class="metric-value">${combinedBenefits.totalSavingsTherm.toLocaleString()} therms/year</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Total Emission Reduction:</span>
                                    <span class="metric-value">${combinedBenefits.totalEmissionReduction.toFixed(1)} tons CO2e/year</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                ` : ''}

                <div class="section">
                    <h2 class="section-title">CPACE Financing Options</h2>
                    <div class="grid">
                        ${cpaceLenders.map(lender => `
                            <div class="card">
                                <h3>${lender.name}</h3>
                                <div class="metric">
                                    <span class="metric-label">Loan Range:</span>
                                    <span class="metric-value">$${lender.minLoanAmount.toLocaleString()} - $${lender.maxLoanAmount.toLocaleString()}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Specialties:</span>
                                    <span class="metric-value">${lender.specialties.join(', ')}</span>
                                </div>
                                ${lender.partnershipContact ? `
                                    <div class="contact-info">
                                        <strong>Partnership Contact:</strong><br>
                                        ${lender.partnershipContact.name}, ${lender.partnershipContact.title}<br>
                                        Email: ${lender.partnershipContact.email}<br>
                                        Phone: ${lender.partnershipContact.phone}
                                        ${lender.partnershipContact.directLine ? `<br>Direct: ${lender.partnershipContact.directLine}` : ''}
                                    </div>
                                    <div class="partnership-benefits">
                                        <strong>Partnership Benefits:</strong><br>
                                        ${lender.partnershipBenefits?.join('<br>• ') || 'Contact for details'}
                                    </div>
                                ` : ''}
                            </div>
                        `).join('')}
                    </div>
                </div>

                <div class="section">
                    <h2 class="section-title">Recommended Contractors</h2>
                    <div class="grid">
                        ${contractors.map(contractor => `
                            <div class="card">
                                <h3>${contractor.name}</h3>
                                <div class="metric">
                                    <span class="metric-label">Specialty:</span>
                                    <span class="metric-value">${contractor.specialty}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Location:</span>
                                    <span class="metric-value">${contractor.location}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Rating:</span>
                                    <span class="metric-value">${contractor.rating}/5.0</span>
                                </div>
                                ${contractor.certifications ? `
                                    <div class="metric">
                                        <span class="metric-label">Certifications:</span>
                                        <span class="metric-value">${contractor.certifications.join(', ')}</span>
                                    </div>
                                ` : ''}
                                ${contractor.partnershipContact ? `
                                    <div class="contact-info">
                                        <strong>Partnership Contact:</strong><br>
                                        ${contractor.partnershipContact.name}, ${contractor.partnershipContact.title}<br>
                                        Email: ${contractor.partnershipContact.email}<br>
                                        Phone: ${contractor.partnershipContact.phone}
                                    </div>
                                    ${contractor.partnershipProgram ? `
                                        <div class="partnership-benefits">
                                            <strong>Partnership Program:</strong><br>
                                            • Referral Commission: ${contractor.partnershipProgram.referralCommission || 'Contact for details'}<br>
                                            • Volume Discounts: ${contractor.partnershipProgram.volumeDiscounts || 'Available'}<br>
                                            • Exclusive Territories: ${contractor.partnershipProgram.exclusiveTerritories ? 'Yes' : 'No'}
                                        </div>
                                    ` : ''}
                                ` : ''}
                            </div>
                        `).join('')}
                    </div>
                </div>

                <div class="section">
                    <h2 class="section-title">Available Grants & Incentives</h2>
                    <div class="grid">
                        ${grantsIncentives.map(grant => `
                            <div class="card">
                                <h3>${grant.name}</h3>
                                <div class="metric">
                                    <span class="metric-label">Type:</span>
                                    <span class="metric-value">${grant.type}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label">Amount:</span>
                                    <span class="metric-value">${grant.amount}</span>
                                </div>
                                <p>${grant.description}</p>
                                <div class="metric">
                                    <span class="metric-label">Eligibility:</span>
                                    <span class="metric-value">${grant.eligibility}</span>
                                </div>
                                ${grant.deadline ? `
                                    <div class="metric">
                                        <span class="metric-label">Deadline:</span>
                                        <span class="metric-value">${grant.deadline}</span>
                                    </div>
                                ` : ''}
                            </div>
                        `).join('')}
                    </div>
                </div>

                <div class="footer">
                    <p>This report was generated by the NYC Energy Retrofit Analyzer</p>
                    <p>For partnerships and business development opportunities, contact the individuals listed above</p>
                    <p>Report generated on ${new Date().toLocaleString()}</p>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const downloadSummaryPDF = async () => {
        if (!selectedBuilding) return;

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 20;
            let yPosition = margin;

            // Helper function to add text with automatic page breaks
            const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
                if (yPosition > pageHeight - margin) {
                    pdf.addPage();
                    yPosition = margin;
                }
                pdf.setFontSize(fontSize);
                pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
                pdf.text(text, margin, yPosition);
                yPosition += fontSize * 0.5 + 3;
            };

            const addSection = (title: string) => {
                yPosition += 5;
                addText(title, 14, true);
                yPosition += 2;
            };

            // Header
            pdf.setFillColor(37, 99, 235);
            pdf.rect(0, 0, pageWidth, 30, 'F');
            pdf.setTextColor(255, 255, 255);
            addText('NYC Energy Retrofit Analyzer', 18, true);
            pdf.setTextColor(0, 0, 0);
            yPosition = 40;

            // Building Information
            addSection('Building Information');
            addText(`Property: ${selectedBuilding.propertyName || selectedBuilding.address}`);
            addText(`Address: ${selectedBuilding.address}`);
            addText(`Borough: ${selectedBuilding.borough}`);
            addText(`Building Type: ${selectedBuilding.buildingType}`);
            addText(`Gross Floor Area: ${selectedBuilding.grossFloorArea.toLocaleString()} sq ft`);
            addText(`Energy Star Score: ${selectedBuilding.energyStarScore || 'N/A'}`);

            // Current Performance
            addSection('Current Energy Performance');
            addText(`Site EUI: ${selectedBuilding.siteEUI} kBtu/sq ft`);
            addText(`Source EUI: ${selectedBuilding.sourceEUI} kBtu/sq ft`);
            addText(`Total GHG Emissions: ${selectedBuilding.totalGHGEmissions.toLocaleString()} tons CO2e`);
            addText(`Electricity Use: ${selectedBuilding.electricityUse.toLocaleString()} kWh`);
            addText(`Natural Gas Use: ${selectedBuilding.naturalGasUse.toLocaleString()} kBtu`);

            // LL97 Analysis
            if (emissionsGap) {
                addSection('Local Law 97 Compliance Analysis');
                addText(`2025 Emission Limit: ${emissionsGap.limitPerSqFt.toFixed(3)} tons CO2e/sq ft`);
                addText(`Current Emissions: ${emissionsGap.currentEmissionsPerSqFt.toFixed(3)} tons CO2e/sq ft`);
                addText(`Gap to Close: ${emissionsGap.gapPerSqFt.toFixed(3)} tons CO2e/sq ft`);
                addText(`Total Gap: ${emissionsGap.totalGap.toFixed(1)} tons CO2e`);
                addText(`Potential Annual Fine: $${emissionsGap.annualFine.toLocaleString()}`);
            }

            // Selected Retrofits
            const selected = retrofitOptions.filter(option => selectedRetrofits.includes(option.id));
            if (selected.length > 0) {
                addSection('Selected Retrofit Measures');
                selected.forEach(retrofit => {
                    addText(`${retrofit.name}`, 12, true);
                    addText(`  Description: ${retrofit.description}`);
                    addText(`  Cost (after incentives): $${getRetrofitCostAfterIncentives(retrofit).toLocaleString()}`);
                    addText(`  Annual kWh Savings: ${retrofit.savingsKwh.toLocaleString()} kWh`);
                    addText(`  Annual Therm Savings: ${retrofit.savingsTherm.toLocaleString()} therms`);
                    addText(`  Emission Reduction: ${retrofit.emissionReduction.toFixed(1)} tons CO2e`);
                    addText(`  Payback Period: ${retrofit.paybackYears.toFixed(1)} years`);
                    addText(`  ROI: ${retrofit.roi.toFixed(1)}%`);
                    yPosition += 3;
                });

                // Combined Benefits
                const combinedBenefits = calculateCombinedBenefits();
                addSection('Combined Package Summary');
                addText(`Total Investment: $${combinedBenefits.totalCost.toLocaleString()}`);
                addText(`Total Energy Savings: ${combinedBenefits.totalSavingsKwh.toLocaleString()} kWh/year`);
                addText(`Total Gas Savings: ${combinedBenefits.totalSavingsTherm.toLocaleString()} therms/year`);
                addText(`Total Emission Reduction: ${combinedBenefits.totalEmissionReduction.toFixed(1)} tons CO2e/year`);
                addText(`Average Payback Period: ${combinedBenefits.averagePayback.toFixed(1)} years`);
            }

            // CPACE Lenders
            addSection('CPACE Financing Partners');
            cpaceLenders.forEach(lender => {
                addText(`${lender.name}`, 11, true);
                addText(`  Website: ${lender.website}`);
                addText(`  Specialties: ${lender.specialties.join(', ')}`);
                addText(`  Loan Range: $${lender.minLoanAmount.toLocaleString()} - $${lender.maxLoanAmount.toLocaleString()}`);
                if (lender.partnershipContact) {
                    addText(`  Partnership Contact: ${lender.partnershipContact.name}, ${lender.partnershipContact.title}`);
                    addText(`  Email: ${lender.partnershipContact.email}`);
                    addText(`  Phone: ${lender.partnershipContact.phone}`);
                }
                yPosition += 2;
            });

            // Contractors
            addSection('Contractor Partners');
            contractors.forEach(contractor => {
                addText(`${contractor.name}`, 11, true);
                addText(`  Specialty: ${contractor.specialty}`);
                addText(`  Location: ${contractor.location}`);
                addText(`  Rating: ${contractor.rating}/5.0`);
                if (contractor.partnershipContact) {
                    addText(`  Partnership Contact: ${contractor.partnershipContact.name}, ${contractor.partnershipContact.title}`);
                    addText(`  Email: ${contractor.partnershipContact.email}`);
                    addText(`  Phone: ${contractor.partnershipContact.phone}`);
                }
                yPosition += 2;
            });

            // Footer
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(8);
                pdf.text(`Report generated on ${new Date().toLocaleString()}`, margin, pageHeight - 10);
                pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 10);
            }

            // Save PDF
            const fileName = `building-analysis-${selectedBuilding.propertyName || selectedBuilding.address}-${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName.replace(/[^a-z0-9]/gi, '_').toLowerCase());

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again or use the print option.');
        }
    };

    const exportPartnershipContacts = () => {
        if (!selectedBuilding) return;

        const contacts = [
            ...cpaceLenders.map(lender => ({
                type: 'CPACE Lender',
                company: lender.name,
                name: lender.partnershipContact?.name || 'Contact via website',
                title: lender.partnershipContact?.title || '',
                email: lender.partnershipContact?.email || 'See website',
                phone: lender.partnershipContact?.phone || 'See website',
                specialties: lender.specialties.join(', '),
                benefits: lender.partnershipBenefits?.join('; ') || 'Contact for details'
            })),
            ...contractors.map(contractor => ({
                type: 'Contractor',
                company: contractor.name,
                name: contractor.partnershipContact?.name || 'Contact via website',
                title: contractor.partnershipContact?.title || '',
                email: contractor.partnershipContact?.email || contractor.website || 'N/A',
                phone: contractor.partnershipContact?.phone || 'See website',
                specialties: contractor.specialty,
                benefits: contractor.partnershipProgram ? 
                    `Commission: ${contractor.partnershipProgram.referralCommission || 'TBD'}; Volume Discounts: ${contractor.partnershipProgram.volumeDiscounts || 'Available'}` : 
                    'Contact for details'
            }))
        ];

        const csv = [
            ['Type', 'Company', 'Contact Name', 'Title', 'Email', 'Phone', 'Specialties', 'Partnership Benefits'].join(','),
            ...contacts.map(contact => [
                contact.type,
                contact.company,
                contact.name,
                contact.title,
                contact.email,
                contact.phone,
                contact.specialties,
                contact.benefits
            ].map(field => `"${field.replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `partnership-contacts-${selectedBuilding.propertyName || 'building'}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const exportBuildingAnalysis = () => {
        if (!selectedBuilding) return;

        const selected = retrofitOptions.filter(option => selectedRetrofits.includes(option.id));
        const combinedBenefits = calculateCombinedBenefits();

        const analysisData = {
            reportMetadata: {
                generatedDate: new Date().toISOString(),
                buildingId: selectedBuilding.id,
                reportVersion: "1.0"
            },
            buildingInformation: {
                propertyName: selectedBuilding.propertyName,
                address: selectedBuilding.address,
                borough: selectedBuilding.borough,
                zipCode: selectedBuilding.zipCode,
                buildingType: selectedBuilding.buildingType,
                primaryPropertyType: selectedBuilding.primaryPropertyType,
                grossFloorArea: selectedBuilding.grossFloorArea,
                energyStarScore: selectedBuilding.energyStarScore
            },
            currentPerformance: {
                siteEUI: selectedBuilding.siteEUI,
                sourceEUI: selectedBuilding.sourceEUI,
                weatherNormalizedSiteEUI: selectedBuilding.weatherNormalizedSiteEUI,
                weatherNormalizedSourceEUI: selectedBuilding.weatherNormalizedSourceEUI,
                totalGHGEmissions: selectedBuilding.totalGHGEmissions,
                directGHGEmissions: selectedBuilding.directGHGEmissions,
                indirectGHGEmissions: selectedBuilding.indirectGHGEmissions,
                electricityUse: selectedBuilding.electricityUse,
                naturalGasUse: selectedBuilding.naturalGasUse
            },
            ll97Analysis: emissionsGap ? {
                limitPerSqFt: emissionsGap.limitPerSqFt,
                currentEmissionsPerSqFt: emissionsGap.currentEmissionsPerSqFt,
                gapPerSqFt: emissionsGap.gapPerSqFt,
                totalGap: emissionsGap.totalGap,
                annualFine: emissionsGap.annualFine,
                complianceStatus: emissionsGap.gapPerSqFt <= 0 ? 'Compliant' : 'Non-Compliant'
            } : null,
            selectedRetrofits: selected.map(retrofit => ({
                id: retrofit.id,
                name: retrofit.name,
                description: retrofit.description,
                grossCost: retrofit.cost,
                costAfterIncentives: getRetrofitCostAfterIncentives(retrofit),
                savingsKwh: retrofit.savingsKwh,
                savingsTherm: retrofit.savingsTherm,
                emissionReduction: retrofit.emissionReduction,
                paybackYears: retrofit.paybackYears,
                roi: retrofit.roi,
                selectedIncentives: selectedIncentives[retrofit.id] || []
            })),
            combinedAnalysis: combinedBenefits,
            partnershipContacts: {
                cpaceLenders: cpaceLenders.map(lender => ({
                    name: lender.name,
                    website: lender.website,
                    specialties: lender.specialties,
                    minLoanAmount: lender.minLoanAmount,
                    maxLoanAmount: lender.maxLoanAmount,
                    portfolioMinimum: lender.portfolioMinimum,
                    partnershipContact: lender.partnershipContact,
                    partnershipBenefits: lender.partnershipBenefits
                })),
                contractors: contractors.map(contractor => ({
                    name: contractor.name,
                    specialty: contractor.specialty,
                    location: contractor.location,
                    rating: contractor.rating,
                    website: contractor.website,
                    certifications: contractor.certifications,
                    partnershipContact: contractor.partnershipContact,
                    partnershipProgram: contractor.partnershipProgram
                }))
            },
            availableIncentives: grantsIncentives
        };

        const jsonString = JSON.stringify(analysisData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `building-analysis-data-${selectedBuilding.propertyName || selectedBuilding.address}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-neutral-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-primary-600 hover:text-primary-800 flex items-center"
                    >
                        ← Back to Building List
                    </button>
                    <h1 className="text-3xl font-bold text-neutral-900">Building Analysis & Retrofit Planning</h1>
                    <p className="text-neutral-600 mt-2">Comprehensive analysis and financing solutions for your building</p>
                    
                    {/* Usage Instructions */}
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                            <span className="mr-2">🔍</span> How to Use Building Analysis Tool
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-semibold text-blue-800 mb-2">Step-by-Step Process:</h4>
                                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
                                    <li>Search and select your building using address or BBL</li>
                                    <li>Review LL97 compliance status and fine risks</li>
                                    <li>Explore retrofit recommendations with ROI analysis</li>
                                    <li>Select optimal retrofit measures for your budget</li>
                                    <li>Connect with contractors and financing partners</li>
                                    <li>Export comprehensive reports for implementation</li>
                                </ol>
                            </div>
                            <div>
                                <h4 className="font-semibold text-blue-800 mb-2">What You'll Get:</h4>
                                <ul className="space-y-1 text-sm text-blue-700">
                                    <li>• <strong>Compliance Status:</strong> Current LL97 standing & penalties</li>
                                    <li>• <strong>Retrofit Options:</strong> Customized efficiency measures</li>
                                    <li>• <strong>Financial Analysis:</strong> Costs, savings, and ROI calculations</li>
                                    <li>• <strong>Incentives:</strong> Available grants and rebates</li>
                                    <li>• <strong>Contractor Network:</strong> Vetted service providers</li>
                                    <li>• <strong>Financing Options:</strong> CPACE and loan alternatives</li>
                                </ul>
                            </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-3">
                            💡 <strong>Pro Tip:</strong> For the most accurate analysis, have your latest utility bills and Energy & Water Use Report ready.
                        </p>
                    </div>
                </div>

                {/* Building Selection Section */}
                <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                    <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                        <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">1</span>
                        Select Building for Analysis
                    </h2>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by property name, address, or BBL number..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setShowSuggestions(true);
                            }}
                            className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {showSuggestions && filteredBuildings.length > 0 && (
                            <div className="absolute z-10 w-full bg-white border border-neutral-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                                {filteredBuildings.slice(0, 10).map((building) => (
                                    <div
                                        key={building.id}
                                        onClick={() => handleBuildingSelect(building)}
                                        className="p-3 hover:bg-neutral-50 cursor-pointer border-b border-neutral-100 last:border-b-0"
                                    >
                                        <div className="font-medium text-neutral-900">{building.propertyName}</div>
                                        <div className="text-sm text-neutral-600">{building.address}, {building.borough}</div>
                                        <div className="text-xs text-neutral-500 mt-1">
                                            ID: {building.id} | {building.primaryPropertyType} | {(building.grossFloorArea || 0).toLocaleString()} sq ft
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Analysis Options Section */}
                {selectedBuilding && (
                    <React.Fragment>
                    <div>
                    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                            <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">2</span>
                            Choose Analysis Type
                        </h2>
                        <div className="space-y-4">
                            <p className="text-neutral-600 mb-6">
                                Select an analysis type to get customized retrofit recommendations and financial projections for your building.
                            </p>
                            
                            {economicChoices.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {economicChoices.map((choice) => (
                                        <div
                                            key={choice.id}
                                            onClick={() => handleAnalysisTypeSelect(choice.id)}
                                            className="bg-white border border-neutral-200 rounded-lg p-6 hover:border-primary-300 hover:shadow-md cursor-pointer transition-all duration-200 group"
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <h3 className="font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors">
                                                    {choice.name}
                                                </h3>
                                                <svg className="w-5 h-5 text-neutral-400 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                            <p className="text-sm text-neutral-600 mb-4">
                                                {choice.description}
                                            </p>
                                            <div className="text-xs text-neutral-500">
                                                <div className="flex items-center justify-between">
                                                    <span>Cost: ${choice.totalCost.toLocaleString()}</span>
                                                    <span>Payback: {choice.paybackPeriod.toFixed(1)} yrs</span>
                                                </div>
                                                <div className="flex items-center justify-between mt-1">
                                                    <span>Risk: {choice.riskLevel}</span>
                                                    <span>ROI: {choice.roi10Year.toFixed(1)}%</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                                    <p className="text-neutral-500">Loading analysis options...</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                        <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">2</span>
                        LL97 Compliance Analysis
                    </h2>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="bg-neutral-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-neutral-900 mb-2">Building Information</h3>
                                {selectedBuilding && (
                                    <div className="space-y-2 text-sm">
                                        <div><span className="font-medium">Property:</span> {selectedBuilding.propertyName}</div>
                                        <div><span className="font-medium">Address:</span> {selectedBuilding.address}</div>
                                        <div><span className="font-medium">Type:</span> {selectedBuilding.buildingType}</div>
                                        <div className="text-xs text-neutral-500 ml-11 -mt-1">
                                            {getBuildingTypeDescription(selectedBuilding.buildingType)}
                                        </div>
                                        <div><span className="font-medium">Size:</span> {selectedBuilding.grossFloorArea?.toLocaleString() || '0'} sq ft</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {emissionsGap && (
                            <div className="space-y-4">
                                <div className={`p-4 rounded-lg ${emissionsGap.complianceStatus === 'Compliant' ? 'bg-success-50' : 'bg-danger-50'}`}>
                                    <h3 className="font-semibold text-neutral-900 mb-2">
                                        Compliance Status: 
                                        <span className={`ml-2 ${emissionsGap.complianceStatus === 'Compliant' ? 'text-success-600' : 'text-danger-600'}`}>
                                            {emissionsGap.complianceStatus}
                                        </span>
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <div><span className="font-medium">Current Emissions:</span> {emissionsGap.currentEmissions?.toFixed(1) || '0.0'} metric tons CO2e</div>
                                        <div><span className="font-medium">Allowed Emissions:</span> {emissionsGap.allowedEmissions?.toFixed(1) || '0.0'} metric tons CO2e</div>
                                        <div><span className="font-medium">Gap:</span> {emissionsGap.gapAmount?.toFixed(1) || '0.0'} metric tons CO2e</div>
                                        <div><span className="font-medium">Annual Fine:</span> ${emissionsGap.annualFine?.toLocaleString() || '0'}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 3: Retrofit Solutions */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                        <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">3</span>
                        Retrofit Solutions & Pricing
                    </h2>
                    
                    <div className="space-y-4">
                        {retrofitOptions.map((option) => (
                            <div key={option.id} className="border border-neutral-200 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedRetrofits.includes(option.id)}
                                            onChange={() => toggleRetrofitSelection(option.id)}
                                            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                                        />
                                        <div>
                                            <h3 className="font-semibold text-neutral-900">{option.name}</h3>
                                            <p className="text-neutral-600 text-sm mt-1">{option.description}</p>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                                                <div className="relative">
                                                    <span className="font-medium text-neutral-700">Cost (after incentives):</span>
                                                    <button
                                                        className="ml-1 text-xs text-primary-500 underline cursor-pointer"
                                                        onClick={() => setShowCalc({ type: 'cost', id: option.id })}
                                                        title="How is this calculated?"
                                                    >
                                                        How?
                                                    </button>
                                                    <div className="text-lg font-bold text-success-600">$${getRetrofitCostAfterIncentives(option).toLocaleString()}</div>
                                                    {showCalc && showCalc.type === 'cost' && showCalc.id === option.id && (
                                                        <div className="absolute z-50 bg-white border border-neutral-300 rounded shadow-lg p-3 mt-2 text-xs text-neutral-700 max-w-xs">
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="font-semibold">Cost Calculation</span>
                                                                <button className="text-neutral-400 ml-2" onClick={() => setShowCalc(null)}>&times;</button>
                                                            </div>
                                                            <pre className="whitespace-pre-wrap">{getCostExplanation(option)}</pre>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="relative">
                                                    <span className="font-medium text-neutral-700">Emission Reduction:</span>
                                                    <button
                                                        className="ml-1 text-xs text-primary-500 underline cursor-pointer"
                                                        onClick={() => setShowCalc({ type: 'emission', id: option.id })}
                                                        title="How is this calculated?"
                                                    >
                                                        How?
                                                    </button>
                                                    <div className="text-lg font-bold text-primary-600">{option.emissionReduction?.toFixed(1) || '0.0'} tons</div>
                                                    {showCalc && showCalc.type === 'emission' && showCalc.id === option.id && (
                                                        <div className="absolute z-50 bg-white border border-neutral-300 rounded shadow-lg p-3 mt-2 text-xs text-neutral-700 max-w-xs">
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="font-semibold">Emission Reduction Calculation</span>
                                                                <button className="text-neutral-400 ml-2" onClick={() => setShowCalc(null)}>&times;</button>
                                                            </div>
                                                            <pre className="whitespace-pre-wrap">{getEmissionReductionExplanation(option)}</pre>
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <span className="font-medium text-neutral-700">Payback:</span>
                                                    <div className="text-lg font-bold text-accent-600">{option.paybackYears?.toFixed(1) || '0.0'} years</div>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-neutral-700">ROI:</span>
                                                    <div className="text-lg font-bold text-warning-600">{option.roi?.toFixed(1) || '0.0'}%</div>
                                                </div>
                                            </div>
                                            {/* Incentives Section */}
                                            <div className="mt-3">
                                                <span className="font-medium text-neutral-700">Eligible Grants & Incentives:</span>
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    {getApplicableIncentives(option).map((incentive) => {
                                                        const actualRebate = getActualRebateAmount(incentive, option);
                                                        return (
                                                            <label key={incentive.name} className="flex items-center space-x-1 bg-primary-50 px-2 py-1 rounded cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={(selectedIncentives[option.id] || []).includes(incentive.name)}
                                                                    onChange={() => handleIncentiveToggle(option.id, incentive.name)}
                                                                    className="text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                                                                />
                                                                <span className="text-xs text-primary-800 font-medium">{incentive.name}</span>
                                                                <span className="text-xs text-neutral-600 ml-1">({incentive.amount}</span>
                                                                {actualRebate && (
                                                                    <span className="text-xs text-success-700 ml-1">Actual: {actualRebate}</span>
                                                                )}
                                                                <span className="text-xs text-neutral-600">)</span>
                                                                {incentive.deadline && (
                                                                    <span className="text-xs text-neutral-500 ml-1">Deadline: {incentive.deadline}</span>
                                                                )}
                                                                {incentive.link ? (
                                                                    <a
                                                                        href={incentive.link}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-xs text-primary-500 underline ml-1"
                                                                    >
                                                                        Details
                                                                    </a>
                                                                ) : (
                                                                    <Link
                                                                        to={`/connection-hub?tab=incentives`}
                                                                        className="text-xs text-primary-500 underline ml-1"
                                                                    >
                                                                        Learn More
                                                                    </Link>
                                                                )}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {selectedRetrofits.length > 0 && (
                            <div className="mt-6 bg-primary-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-neutral-900 mb-3">Combined Package Summary</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                        <span className="font-medium text-neutral-700">Total Investment:</span>
                                        <div className="text-xl font-bold text-success-600">${combinedBenefits.totalCost?.toLocaleString() || '0'}</div>
                                    </div>
                                    <div>
                                        <span className="font-medium text-neutral-700">Total Emission Reduction:</span>
                                        <div className="text-xl font-bold text-primary-600">{combinedBenefits.totalEmissionReduction?.toFixed(1) || '0.0'} tons</div>
                                    </div>
                                    <div>
                                        <span className="font-medium text-neutral-700">Average Payback:</span>
                                        <div className="text-xl font-bold text-accent-600">{combinedBenefits.averagePayback?.toFixed(1) || '0.0'} years</div>
                                    </div>
                                    <div>
                                        <span className="font-medium text-neutral-700">Annual Savings:</span>
                                        <div className="text-xl font-bold text-warning-600">${(combinedBenefits.totalCost && combinedBenefits.averagePayback ? (combinedBenefits.totalCost / combinedBenefits.averagePayback).toLocaleString() : '0')}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Step 4: Calculation Methodology */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                            <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">4</span>
                            Calculation Methodology
                        </h2>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-2">LL97 Fine Calculation</h3>
                                    <div className="bg-neutral-50 p-3 rounded text-sm font-mono">
                                        <div>1. Current emissions: {emissionsGap?.currentEmissions?.toFixed(1) || '0.0'} tons CO2e</div>
                                        <div>2. Allowed emissions: {emissionsGap?.allowedEmissions?.toFixed(1) || '0.0'} tons CO2e</div>
                                        <div>3. Excess emissions: {emissionsGap?.gapAmount?.toFixed(1) || '0.0'} tons</div>
                                        <div>4. Fine rate: $268 per excess ton</div>
                                        <div className="border-t pt-1 mt-1">
                                            <strong>Annual fine: ${emissionsGap?.annualFine?.toLocaleString() || '0'}</strong>
                                        </div>
                                        <div className="text-xs text-neutral-600 mt-1">
                                            Formula: (Current - Allowed) × $268
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-2">Energy Cost Savings</h3>
                                    <div className="bg-neutral-50 p-3 rounded text-sm font-mono">
                                        <div>Electricity rate: $0.20/kWh</div>
                                        <div>Natural gas rate: $1.50/therm</div>
                                        <div>Annual escalation: 3%</div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-2">ROI Calculation</h3>
                                    <div className="bg-neutral-50 p-3 rounded text-sm font-mono">
                                        <div>ROI = (Annual Savings / Investment) × 100</div>
                                        <div>Payback = Investment / Annual Savings</div>
                                        <div>NPV calculated at 6% discount rate</div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-2">Incentive Impact Analysis</h3>
                                    <div className="bg-neutral-50 p-3 rounded text-sm font-mono">
                                        <div>Federal ITC: 30% of project cost</div>
                                        <div>NYSERDA: $2/sq ft, max $200,000</div>
                                        <div>Con Edison: Up to $50,000</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 5: Energy Savings Projections */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                            <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">5</span>
                            Energy Savings Projections
                        </h2>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-2">Annual Energy Savings</h3>
                                    <div className="bg-neutral-50 p-3 rounded text-sm font-mono">
                                        <div>Electricity: {combinedBenefits.totalEmissionReduction ? (combinedBenefits.totalEmissionReduction * 2500).toFixed(0) : '0'} kWh</div>
                                        <div>Natural Gas: {combinedBenefits.totalEmissionReduction ? (combinedBenefits.totalEmissionReduction * 50).toFixed(0) : '0'} therms</div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-2">Cumulative Savings (5 years)</h3>
                                    <div className="bg-neutral-50 p-3 rounded text-sm font-mono">
                                        <div>Electricity: {combinedBenefits.totalEmissionReduction ? (combinedBenefits.totalEmissionReduction * 2500 * 5).toFixed(0) : '0'} kWh</div>
                                        <div>Natural Gas: {combinedBenefits.totalEmissionReduction ? (combinedBenefits.totalEmissionReduction * 50 * 5).toFixed(0) : '0'} therms</div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-2">Cost Savings Breakdown</h3>
                                    <div className="bg-neutral-50 p-3 rounded text-sm font-mono">
                                        {(() => {
                                            const annualSavings = combinedBenefits.totalCost && combinedBenefits.averagePayback 
                                                ? combinedBenefits.totalCost / combinedBenefits.averagePayback 
                                                : 0;
                                            return (
                                                <>
                                                    <div>Year 1: ${annualSavings.toLocaleString()}</div>
                                                    <div>Year 2: ${(annualSavings * 1.03).toLocaleString()}</div>
                                                    <div>Year 3: ${(annualSavings * 1.06).toLocaleString()}</div>
                                                    <div>Year 4: ${(annualSavings * 1.09).toLocaleString()}</div>
                                                    <div>Year 5: ${(annualSavings * 1.13).toLocaleString()}</div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-2">Emissions Reduction Timeline</h3>
                                    <div className="bg-neutral-50 p-3 rounded text-sm font-mono">
                                        <div>2025: {combinedBenefits.totalEmissionReduction?.toFixed(1) || '0.0'} tons CO2e</div>
                                        <div>2030: {combinedBenefits.totalEmissionReduction?.toFixed(1) || '0.0'} tons CO2e</div>
                                        <div>2035: {combinedBenefits.totalEmissionReduction?.toFixed(1) || '0.0'} tons CO2e</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 6: CPACE Financing */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                            <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">6</span>
                            CPACE Financing Options
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {cpaceLenders.map((lender, index) => (
                                <div key={index} className="border border-neutral-200 rounded-lg p-4">
                                    <h3 className="font-semibold text-neutral-900 mb-2">{lender.name}</h3>
                                    <p className="text-sm text-neutral-600 mb-3">
                                        Loan Range: ${lender.minLoanAmount?.toLocaleString() || '0'} - ${lender.maxLoanAmount?.toLocaleString() || '0'}
                                    </p>
                                    <div className="mb-3">
                                        <h4 className="font-medium text-neutral-700 text-sm mb-1">Specialties:</h4>
                                        <div className="flex flex-wrap gap-1">
                                            {lender.specialties.map((specialty, idx) => (
                                                <span key={idx} className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded">
                                                    {specialty}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <a 
                                        href={lender.website} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary-600 hover:text-primary-800 text-sm"
                                    >
                                        Visit Website →
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Step 7: Contractors */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                            <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">7</span>
                            Recommended Contractors
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {contractors.map((contractor, index) => (
                                <div key={index} className="border border-neutral-200 rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-semibold text-neutral-900">{contractor.name}</h3>
                                        <div className="flex items-center">
                                            <span className="text-warning-400">★</span>
                                            <span className="text-sm text-neutral-600 ml-1">{contractor.rating}</span>
                                        </div>
                                    </div>
                                    <p className="text-sm text-neutral-600 mb-1">{contractor.specialty}</p>
                                    <p className="text-sm text-neutral-500 mb-3">{contractor.location}</p>
                                    {contractor.website && (
                                        <a 
                                            href={contractor.website} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-primary-600 hover:text-primary-800 text-sm"
                                        >
                                            Visit Website →
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Step 8: Grants and Incentives */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                            <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">8</span>
                            Grants & Incentives
                        </h2>
                        
                        <div className="space-y-4">
                            {grantsIncentives.map((grant, index) => (
                                <div key={index} className="border border-neutral-200 rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-semibold text-neutral-900">{grant.name}</h3>
                                        <span className={`px-2 py-1 text-xs rounded font-medium ${
                                            grant.type === 'Federal' ? 'bg-primary-100 text-primary-800' :
                                            grant.type === 'State' ? 'bg-success-100 text-success-800' :
                                            grant.type === 'City' ? 'bg-accent-100 text-accent-800' :
                                            'bg-warning-100 text-warning-800'
                                        }`}>
                                            {grant.type}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-neutral-600 mb-2">{grant.description}</p>
                                            <p className="text-sm"><span className="font-medium">Amount:</span> {grant.amount}</p>
                                            {grant.deadline && (
                                                <p className="text-sm text-danger-600"><span className="font-medium">Deadline:</span> {grant.deadline}</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm"><span className="font-medium">Eligibility:</span> {grant.eligibility}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Export Summary Section */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                            <span className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center text-primary-600 font-bold text-sm inline-block mr-3">9</span>
                            Export Analysis Summary
                        </h2>
                        
                        <div className="space-y-4">
                            <p className="text-neutral-600">Generate a comprehensive printable report including building analysis, retrofit recommendations, financing options, and partnership contacts.</p>
                            
                            <div className="flex flex-wrap gap-4">
                                <button
                                    onClick={() => generatePrintableSummary()}
                                    className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                                    </svg>
                                    Generate Full Summary
                                </button>
                                
                                <button
                                    onClick={() => window.print()}
                                    className="bg-info-600 text-white px-6 py-2 rounded-lg hover:bg-info-700 transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                    </svg>
                                    Print Analysis Report
                                </button>
                                
                                <button
                                    onClick={() => downloadSummaryPDF()}
                                    className="bg-success-600 text-white px-6 py-2 rounded-lg hover:bg-success-700 transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Download PDF Report
                                </button>
                                
                                <button
                                    onClick={() => exportPartnershipContacts()}
                                    className="bg-accent-600 text-white px-6 py-2 rounded-lg hover:bg-accent-700 transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    Export Partnership Contacts
                                </button>
                                
                                <button
                                    onClick={() => exportBuildingAnalysis()}
                                    className="bg-warning-600 text-white px-6 py-2 rounded-lg hover:bg-warning-700 transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h18M3 9h18M3 15h18M3 21h18" />
                                    </svg>
                                    Export Building Analysis Data
                                </button>
                                
                                <button
                                    onClick={() => navigate(`/economic-optimizer/${selectedBuilding?.id}`)}
                                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                                    disabled={!selectedBuilding}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    Economic Optimizer
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                    </div>
                </React.Fragment>
                )}
            </div>
        </div>
    );
};

export default BuildingDetailPage;
